import { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import useFormInstance from 'antd/es/form/hooks/useFormInstance';
import dayjs from 'dayjs';
import { StyledDeliveryConditionsForm } from './style';
import { Label } from 'ui/Label';
import { InputNumber } from 'ui/InputNumber';
import { DatePicker } from 'ui/DatePicker';
import EnumSelect from '../../../../admin/Selects/EnumSelect';
import { DeliveryConditionsFormProps } from '../../../../types/ListingData';

export const DeliveryConditionsForm = ({
  formData,
  isEditing = false,
  onFormDataChange,
}: DeliveryConditionsFormProps) => {
  const intl = useIntl();
  const form = useFormInstance();
  const [contractPeriodType, setContractPeriodType] = useState<string | null>(
    null
  );

  useEffect(() => {
    if (formData?.contract_period_type) {
      setContractPeriodType(formData.contract_period_type);
    }
  }, [formData]);

  useEffect(() => {
    if (formData) {
      form.setFieldsValue({
        contract_period_type: formData.contract_period_type,
        contract_start_date: formData.contract_start_date
          ? dayjs(formData.contract_start_date)
          : undefined,
        contract_end_date: formData.contract_end_date
          ? dayjs(formData.contract_end_date)
          : undefined,
        delivery_period_value: formData.delivery_period_value,
        delivery_period_unit: formData.delivery_period_unit,
        delivery_frequency_value: formData.delivery_frequency_value,
        delivery_frequency_unit: formData.delivery_frequency_unit,
      });
    }
  }, [formData, form]);

  const handleFormChange = () => {
    const currentFormData = form.getFieldsValue(true);

    const dataWithId = {
      ...currentFormData,
      ...(formData?.id && { id: formData.id }),
    };

    onFormDataChange?.(dataWithId);
  };

  const handleContractPeriodTypeChange = (value: string) => {
    setContractPeriodType(value);

    if (value === 'time_period') {
      form.setFieldValue('delivery_period_value', undefined);
      form.setFieldValue('delivery_period_unit', undefined);
    } else if (value === 'from_contract_signing') {
      form.setFieldValue('contract_start_date', undefined);
      form.setFieldValue('contract_end_date', undefined);
    }

    handleFormChange();
  };

  return (
    <StyledDeliveryConditionsForm>
      <div className="form-section">
        <Label
          title={true}
          label={intl.formatMessage({
            id: 'listings.form.delivery_conditions',
          })}
        />

        <div className="form-row">
          <div className="form-field">
            <EnumSelect
              name="contract_period_type"
              code="ListingDeliveryContractType"
              label={intl.formatMessage({
                id: 'listings.form.contract_period_type',
              })}
              onChange={handleContractPeriodTypeChange}
            />
          </div>
        </div>

        {contractPeriodType === 'time_period' && (
          <div className="form-row">
            <div className="form-field">
              <DatePicker
                name="contract_start_date"
                label={intl.formatMessage({
                  id: 'listings.form.start_date',
                })}
                onChange={handleFormChange}
              />
            </div>
            <div className="form-field">
              <DatePicker
                name="contract_end_date"
                label={intl.formatMessage({
                  id: 'listings.form.end_date',
                })}
                onChange={handleFormChange}
                rules={[
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      const startDate = getFieldValue('contract_start_date');
                      if (!value || !startDate) {
                        return Promise.resolve();
                      }
                      if (
                        value.isBefore(startDate) ||
                        value.isSame(startDate)
                      ) {
                        return Promise.reject(
                          new Error(
                            intl.formatMessage({
                              id: 'validation.end_date_after_start',
                            })
                          )
                        );
                      }
                      return Promise.resolve();
                    },
                  }),
                ]}
              />
            </div>
          </div>
        )}

        {contractPeriodType === 'from_contract_signing' && (
          <div className="form-row">
            <div className="form-field">
              <div className="quantity-and-unit">
                <InputNumber
                  className={'quantity-input'}
                  name="delivery_period_value"
                  label={intl.formatMessage({
                    id: 'listings.form.delivery_period',
                  })}
                  controls={false}
                  onChange={handleFormChange}
                />
                <EnumSelect
                  name="delivery_period_unit"
                  code="ListingDeliveryPeriodUnit"
                  onChange={handleFormChange}
                />
              </div>
            </div>
          </div>
        )}

        <div className="form-row">
          <div className="form-field">
            <div className="quantity-and-unit">
              <div className="quantity-input">
                <InputNumber
                  name="delivery_frequency_value"
                  label={intl.formatMessage({
                    id: 'listings.form.delivery_frequency',
                  })}
                  controls={false}
                  onChange={handleFormChange}
                />
              </div>
              <EnumSelect
                name="delivery_frequency_unit"
                code="ListingDeliveryFrequencyUnit"
                onChange={handleFormChange}
              />
            </div>
          </div>
        </div>
      </div>
    </StyledDeliveryConditionsForm>
  );
};
